import React, { useRef, useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { Message, MessageResponse } from 'models/Message';
import { client, consoleLogInDev } from 'shared';
import { useHistory } from 'react-router-dom';
import { useUserContext } from 'UserContext';
import ConversationView from './ConversationView';
import MessageInput from './MessageInput';
import { IProvider } from './models';
import { UserContextUser } from 'models';

interface ISitterChatProps {
    providerIdObject: IProvider;
    onMessageSent?: () => void;
    providerName?: string;
    completedBackgroundCheck: boolean;
    providerImage?: string;
}

const ProviderMessenger = ({ providerIdObject, onMessageSent, providerName, providerImage }: ISitterChatProps) => {
    const bottomOfThread = useRef<null | HTMLDivElement>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [locked, setLocked] = useState(false);
    const [messageToSend, setMessageToSend] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const isMobile = useMediaQuery('(max-width:700px)');
    const mounted = useRef(true);

    const { user } = useUserContext();

    const history = useHistory<{ message: string; navToScreen: string; navToScreenText: string }>();

    useEffect(() => {
        if (history.location?.state?.message) {
            setMessageToSend(history.location.state.message);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        load()
            .then(() => poll())
            .catch(consoleLogInDev);
    }, [providerIdObject?.user?.id]);

    const load = async () => {
        if (providerIdObject) {
            client(`api/messages/refresh-thread/?one=${providerIdObject.user.id}&limit=20`).then(
                (result: MessageResponse) => {
                    setMessages(result?.messages?.reverse());
                    setLocked(result?.locked);
                },
            );
        }
    };

    const poll = async () => {
        while (mounted.current) {
            await load();
            await new Promise((r) => setTimeout(r, 5000));
        }
    };

    useEffect(() => {
        bottomOfThread.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = () => {
        if (providerIdObject) {
            setIsSendingMessage(true);
            const body = {
                sender: user?.id,
                reciever: providerIdObject.user.id,
                message: messageToSend,
                message_type: providerIdObject.user.id === 240 ? 'PARENT_PORTAL' : 'DURING_SHIFT_CHAT_MESSAGE',
                image: '',
                navigation_screen: history.location?.state?.navToScreen || undefined,
                navigation_button_text: history.location?.state?.navToScreenText || undefined,
            };

            client('api/messages/message-user/', { body })
                .then(() => {
                    onMessageSent?.();
                })
                .catch((e) => {
                    consoleLogInDev(e);
                })
                .finally(() => {
                    setIsSendingMessage(false);
                    setMessageToSend('');
                    load();
                });
        }
    };

    return (
        <ConversationView
            providerIdObject={providerIdObject}
            providerName={providerName}
            messages={messages}
            locked={locked}
            user={user as UserContextUser}
            bottomOfThread={bottomOfThread}
            isMobile={isMobile}
            providerImage={providerImage}
            messageInput={
                <MessageInput
                    setMessageToSend={setMessageToSend}
                    messageToSend={messageToSend}
                    sendMessage={sendMessage}
                    isSendingMessage={isSendingMessage}
                    messagesEndRef={bottomOfThread}
                />
            }
        />
    );
};

export default ProviderMessenger;
